import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  alpha,
  useTheme,
  Fade,
  Tooltip
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import PublicIcon from '@mui/icons-material/Public';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

interface ProjectCardProps {
  project: {
    id: string;
    name: string;
    number: string;
    street: string;
    active: boolean;
    createdAt: string;
    updatedAt: string;
    city?: { id: string; name: string } | null;
    country?: { id: string; name: string; code: string } | null;
    region?: { id: string; name: string; code: string } | null;
  };
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  isToggling: boolean;
}

// Dummy images for slideshow
const DUMMY_IMAGES = [
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1460472178825-e5240623afd5?w=800&h=600&fit=crop',
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop'
];

const flip = keyframes`
  0% { transform: perspective(1000px) rotateY(0deg); }
  50% { transform: perspective(1000px) rotateY(180deg); }
  100% { transform: perspective(1000px) rotateY(360deg); }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 8px 32px rgba(255, 107, 157, 0.15);
  }
  50% {
    box-shadow: 0 8px 32px rgba(255, 107, 157, 0.35), 0 0 30px rgba(255, 107, 157, 0.2);
  }
`;

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  height: '100%',
  borderRadius: 20,
  overflow: 'hidden',
  background: `linear-gradient(135deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#A770EF', 0.05)} 100%)`,
  border: `2px solid ${alpha('#A770EF', 0.2)}`,
  boxShadow: `0 8px 32px ${alpha('#A770EF', 0.15)}`,
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  transformStyle: 'preserve-3d',
  animation: `${slideIn} 0.6s ease-out`,

  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    boxShadow: `0 20px 60px ${alpha('#A770EF', 0.3)}, 0 0 40px ${alpha('#A770EF', 0.2)}`,
    border: `2px solid ${alpha('#A770EF', 0.5)}`,
  },

  '&.flipped': {
    animation: `${flip} 0.8s ease-in-out`,
  }
}));

const ImageSlideshow = styled(Box)({
  position: 'relative',
  height: 240,
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #A770EF 0%, #6E48AA 100%)',

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease',
  },

  '&:hover img': {
    transform: 'scale(1.1) rotate(2deg)',
  }
});

const SlideButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  background: `${alpha('#FFFFFF', 0.9)} !important`,
  backdropFilter: 'blur(10px)',
  border: `2px solid ${alpha('#A770EF', 0.3)}`,
  boxShadow: `0 4px 12px ${alpha('#000000', 0.2)}`,
  zIndex: 2,
  transition: 'all 0.3s ease',

  '&:hover': {
    background: `${alpha('#A770EF', 0.95)} !important`,
    transform: 'translateY(-50%) scale(1.15)',
    boxShadow: `0 6px 20px ${alpha('#A770EF', 0.4)}`,

    '& .MuiSvgIcon-root': {
      color: '#FFFFFF',
    }
  },

  '&.prev': {
    left: 8,
  },

  '&.next': {
    right: 8,
  }
}));

const ImageIndicators = styled(Box)({
  position: 'absolute',
  bottom: 12,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 8,
  zIndex: 2,
});

const Indicator = styled(Box)<{ active: boolean }>(({ active }) => ({
  width: active ? 32 : 8,
  height: 8,
  borderRadius: 4,
  background: active
    ? 'linear-gradient(90deg, #A770EF 0%, #6E48AA 100%)'
    : alpha('#FFFFFF', 0.5),
  border: `2px solid ${alpha('#FFFFFF', 0.8)}`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  boxShadow: `0 2px 8px ${alpha('#000000', 0.3)}`,

  '&:hover': {
    background: active
      ? 'linear-gradient(90deg, #A770EF 0%, #6E48AA 100%)'
      : alpha('#FFFFFF', 0.8),
  }
}));

const GlassOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(180deg, ${alpha('#000000', 0.1)} 0%, ${alpha('#000000', 0.6)} 100%)`,
  zIndex: 1,
});

const StatusBadge = styled(Chip)<{ active: boolean }>(({ active }) => ({
  position: 'absolute',
  top: 16,
  right: 16,
  zIndex: 3,
  background: active
    ? 'linear-gradient(135deg, #00D9C0 0%, #00FFF0 100%)'
    : 'linear-gradient(135deg, #FF5E78 0%, #C44569 100%)',
  color: '#FFFFFF',
  fontWeight: 800,
  border: `2px solid ${alpha('#FFFFFF', 0.8)}`,
  boxShadow: `0 4px 16px ${alpha(active ? '#00D9C0' : '#FF5E78', 0.5)}`,
  fontSize: '0.7rem',
  height: 28,
}));

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onEdit,
  onDelete,
  onToggleActive,
  isToggling
}) => {
  const theme = useTheme();
  const [flipped, setFlipped] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  const handleFlip = () => {
    if (!flipped) {
      setFlipped(true);
      setTimeout(() => {
        setShowDetails(!showDetails);
        setFlipped(false);
      }, 400);
    }
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? DUMMY_IMAGES.length - 1 : prev - 1));
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === DUMMY_IMAGES.length - 1 ? 0 : prev + 1));
  };

  const handleIndicatorClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <StyledCard className={flipped ? 'flipped' : ''} onClick={handleFlip}>
      {/* Front Side */}
      {!showDetails && (
        <>
          <ImageSlideshow>
            <img src={DUMMY_IMAGES[currentImageIndex]} alt={project.name} />
            <GlassOverlay />

            <StatusBadge
              active={project.active}
              label={project.active ? 'Active' : 'Inactive'}
              size="small"
            />

            <SlideButton className="prev" size="small" onClick={handlePrevImage}>
              <NavigateBeforeIcon />
            </SlideButton>

            <SlideButton className="next" size="small" onClick={handleNextImage}>
              <NavigateNextIcon />
            </SlideButton>

            <ImageIndicators>
              {DUMMY_IMAGES.map((_, index) => (
                <Indicator
                  key={index}
                  active={index === currentImageIndex}
                  onClick={(e) => handleIndicatorClick(e, index)}
                />
              ))}
            </ImageIndicators>

            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                right: 16,
                zIndex: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 900,
                  textShadow: `0 2px 8px ${alpha('#000000', 0.6)}`,
                  fontSize: '1.3rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {project.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 600,
                  textShadow: `0 2px 4px ${alpha('#000000', 0.5)}`,
                  fontFamily: 'monospace',
                }}
              >
                #{project.number}
              </Typography>
            </Box>
          </ImageSlideshow>

          <CardContent sx={{ p: 3 }}>
            {project.street && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOnIcon sx={{ color: '#A770EF', fontSize: 20 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {project.street}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {project.city && (
                <Chip
                  icon={<LocationCityIcon sx={{ fontSize: 16 }} />}
                  label={project.city.name}
                  size="small"
                  sx={{
                    background: `linear-gradient(135deg, ${alpha('#4E9FFF', 0.15)} 0%, ${alpha('#00D9C0', 0.1)} 100%)`,
                    color: '#4E9FFF',
                    fontWeight: 700,
                    border: `1px solid ${alpha('#4E9FFF', 0.3)}`,
                  }}
                />
              )}
              {project.country && (
                <Chip
                  icon={<PublicIcon sx={{ fontSize: 16 }} />}
                  label={`${project.country.name} (${project.country.code})`}
                  size="small"
                  sx={{
                    background: `linear-gradient(135deg, ${alpha('#FFA952', 0.15)} 0%, ${alpha('#FF6B9D', 0.1)} 100%)`,
                    color: '#FFA952',
                    fontWeight: 700,
                    border: `1px solid ${alpha('#FFA952', 0.3)}`,
                  }}
                />
              )}
            </Box>

            {project.region && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mb: 2,
                  fontWeight: 600,
                }}
              >
                Region: {project.region.name} ({project.region.code})
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                Created: {formatDate(project.createdAt)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Tooltip title={project.active ? 'Deactivate' : 'Activate'}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleActive();
                  }}
                  disabled={isToggling}
                  sx={{
                    background: project.active
                      ? `linear-gradient(135deg, ${alpha('#00D9C0', 0.15)} 0%, ${alpha('#00FFF0', 0.1)} 100%)`
                      : `linear-gradient(135deg, ${alpha('#FF5E78', 0.15)} 0%, ${alpha('#C44569', 0.1)} 100%)`,
                    color: project.active ? '#00D9C0' : '#FF5E78',
                    border: `2px solid ${alpha(project.active ? '#00D9C0' : '#FF5E78', 0.3)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: project.active
                        ? 'linear-gradient(135deg, #00D9C0 0%, #00FFF0 100%)'
                        : 'linear-gradient(135deg, #FF5E78 0%, #C44569 100%)',
                      color: '#FFFFFF',
                      transform: 'scale(1.1)',
                      boxShadow: `0 4px 12px ${alpha(project.active ? '#00D9C0' : '#FF5E78', 0.4)}`,
                    },
                  }}
                >
                  {project.active ? <ToggleOnIcon fontSize="small" /> : <ToggleOffIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Project">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  sx={{
                    background: `linear-gradient(135deg, ${alpha('#A770EF', 0.15)} 0%, ${alpha('#6E48AA', 0.1)} 100%)`,
                    color: '#A770EF',
                    border: `2px solid ${alpha('#A770EF', 0.3)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #A770EF 0%, #6E48AA 100%)',
                      color: '#FFFFFF',
                      transform: 'scale(1.1) rotate(5deg)',
                      boxShadow: `0 4px 12px ${alpha('#A770EF', 0.4)}`,
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Project">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  sx={{
                    background: `linear-gradient(135deg, ${alpha('#FF5E78', 0.15)} 0%, ${alpha('#C44569', 0.1)} 100%)`,
                    color: '#FF5E78',
                    border: `2px solid ${alpha('#FF5E78', 0.3)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF5E78 0%, #C44569 100%)',
                      color: '#FFFFFF',
                      transform: 'scale(1.1) rotate(-5deg)',
                      boxShadow: `0 4px 12px ${alpha('#FF5E78', 0.4)}`,
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </>
      )}

      {/* Back Side - Detailed Info */}
      {showDetails && (
        <Fade in={showDetails} timeout={400}>
          <Box
            sx={{
              p: 3,
              height: '100%',
              background: `linear-gradient(135deg, ${alpha('#A770EF', 0.08)} 0%, ${alpha('#6E48AA', 0.05)} 100%)`,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #A770EF 0%, #6E48AA 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 6px 20px ${alpha('#A770EF', 0.4)}`,
                }}
              >
                <AccountTreeIcon sx={{ fontSize: 32, color: '#FFFFFF' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #A770EF 0%, #6E48AA 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Project Details
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Click card to flip back
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              <DetailRow label="Project Name" value={project.name} />
              <DetailRow label="Project Number" value={project.number} />
              <DetailRow label="Street" value={project.street || 'Not specified'} />
              <DetailRow label="City" value={project.city?.name || 'Not assigned'} />
              <DetailRow label="Country" value={project.country ? `${project.country.name} (${project.country.code})` : 'Not assigned'} />
              <DetailRow label="Region" value={project.region ? `${project.region.name} (${project.region.code})` : 'Not assigned'} />
              <DetailRow label="Created Date" value={formatDate(project.createdAt)} />
              <DetailRow label="Last Updated" value={formatDate(project.updatedAt)} />
              <DetailRow
                label="Status"
                value={
                  <Chip
                    label={project.active ? 'Active' : 'Inactive'}
                    size="small"
                    sx={{
                      background: project.active
                        ? 'linear-gradient(135deg, #00D9C0 0%, #00FFF0 100%)'
                        : 'linear-gradient(135deg, #FF5E78 0%, #C44569 100%)',
                      color: '#FFFFFF',
                      fontWeight: 700,
                    }}
                  />
                }
              />
            </Box>
          </Box>
        </Fade>
      )}
    </StyledCard>
  );
};

const DetailRow: React.FC<{ label: string; value: string | React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.5,
      borderBottom: `1px solid ${alpha('#A770EF', 0.1)}`,
    }}
  >
    <Typography
      variant="body2"
      sx={{
        fontWeight: 700,
        color: '#00D9C0',
        fontSize: '0.85rem',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: 'text.primary',
        fontSize: '0.85rem',
        textAlign: 'right',
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default ProjectCard;
